import { useQuery, useMutation } from "@apollo/client";
import { Group, Grid, Stack, Title } from "@mantine/core";
import { useMediaQuery } from '@mantine/hooks';
import React, { useState, useEffect} from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import OrderBook from "../../components/symbol-chart/OrderBook/OrderBook";
import SymbolChart from "../../components/symbol-chart/SymbolChart/SymbolChart";
import SpotComponent from "../../components/trades/SpotComponent/SpotComponent";
import UserOrdersComponent from "../../components/trades/UserOrdersComponent/UserOrdersComponent";
import CodeEditor from "../../components/trades/CodeEditor/CodeEditor";
import ShortPositionsComponent from "../../components/trades/ShortPositionsComponent/ShortPositionsComponent";
import LongPositionsComponent from "../../components/trades/LongPositionsComponent/LongPositionsComponent";
import { SYMBOLS } from "../../constants/symbols";
import { CURRENT_USER } from "../../queries/auth";
import "./SymbolPage.css";
import { ADD_LOG } from "../../queries/user-analytics"; // added fro tracking data
import { logPageView} from "../../helpers/useGA"

export default function SymbolPage(props) {
  const { loading, error, data } = useQuery(CURRENT_USER);
  const [userid, setUserid] = useState(null);
  const { code } = useParams();
  const location = useLocation();
  const symbol = SYMBOLS.find((x) => x.code === code);
  const [closedOrders, setClosedOrders] = useState([]);
  const [openAskOrders, setOpenAskOrders] = useState([]);
  const [openBidOrders, setOpenBidOrders] = useState([]);
  const [coverPositionData, setCoverPositionData] = useState(null);
  const [sellPositionData, setSellPositionData] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1090px)');
     // added for tracking user data
    const [logEventMutation] = useMutation(ADD_LOG, {
    onCompleted: ({ addLog }) => {
        // Access the HTTP response directly
        const { status, message } = addLog;
        if (status === "success") {
            console.log("Event logged successfully:", message);
        } else {
            console.error("Failed to log event:", message);
        }
    },
    onError: (error) => {
        console.error("Error logging event:", error.message);
    }
  });
  const logEvent = (eventType, additionalData, _userid) => {
    const eventData = {
        userid: Number(_userid),
        event_type: eventType,
        timestamp: new Date().toISOString(),
        additional_data: JSON.stringify(additionalData)

    };

    logEventMutation({ variables: eventData });
  };

  useEffect(() => {
    if (loading) console.log('Loading user...')
    else if (error || !data?.currentUser) { navigate('/login', { replace: true }) }
    else {
      // console.log(data);
      setUserid(data.currentUser.userid);
      logEvent(`TradesPage for Symbol - ${symbol.name}`, data.currentUser,  data.currentUser.userid);
      const pageTitle = `Trades Page for symbol  - ${symbol.name}`;
      document.title = pageTitle; // Update document title
      logPageView(location.pathname + location.search, pageTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, data, symbol.name, location.pathname, location.search])

  // Handle cover position from short positions component
  const handleCoverPosition = (position) => {
    console.log('Cover position clicked:', position);
    setCoverPositionData({
      symbol: position.symbol,
      quantity: position.quantity,
      entryPrice: position.entryPrice,
      positionId: position.id,
    });
  };

  // Handle sell position from long positions (holdings) component
  const handleSellPosition = (position) => {
    console.log('Sell position clicked:', position);
    setSellPositionData({
      symbol: position.symbol,
      quantity: position.quantity,
      entryPrice: position.entryPrice ?? null,
      positionId: position.id,
    });
  };

  return (
    <div>
      <Group className="back-to-watchlist">
        <Link to="/trade" style={{ color: "#afafaf" }}>
          ◀ &nbsp;Back to Watchlist
        </Link>
      </Group>
      <Group className="group">
        {symbol.icon}
        <Title className="title" order={2}>
          Trade {symbol.name} ({symbol.symbol})
        </Title>
      </Group>
      <Stack className="symbol-page" spacing={18}>
        <Grid columns={24}>
          {!isMobile && <Grid.Col span={16}>
            <div className="chart-wrapper">
              <SymbolChart className="iframe-element" symbol={symbol} />
            </div>
          </Grid.Col>}
          {isMobile && <Grid.Col span={24}>
            <div className="chart-wrapper">
              <SymbolChart className="iframe-element" symbol={symbol} />
            </div>
          </Grid.Col>}
          {!isMobile && <Grid.Col span={8}>
            <div className="spot-component-wrapper-white">
              <SpotComponent
                symbol={symbol.code}
                ownerId={parseInt(userid)}
                walletId={parseInt(userid)}
                setClosedOrders={setClosedOrders}
                setOpenAskOrders={setOpenAskOrders}
                setOpenBidOrders={setOpenBidOrders}
                isActive={isActive}
                setIsActive={setIsActive}
                coverIntent={coverPositionData}
                onCoverIntentHandled={() => setCoverPositionData(null)}
                sellIntent={sellPositionData}
                onSellIntentHandled={() => setSellPositionData(null)}
              />
            </div>
          </Grid.Col>}
          {isMobile && <Grid.Col span={24}>
            <div className="spot-component-wrapper-white">
              <SpotComponent
                symbol={symbol.code}
                ownerId={parseInt(userid)}
                walletId={parseInt(userid)}
                setClosedOrders={setClosedOrders}
                setOpenAskOrders={setOpenAskOrders}
                setOpenBidOrders={setOpenBidOrders}
                isActive={isActive}
                setIsActive={setIsActive}
                coverIntent={coverPositionData}
                onCoverIntentHandled={() => setCoverPositionData(null)}
                sellIntent={sellPositionData}
                onSellIntentHandled={() => setSellPositionData(null)}
              />
            </div>
          </Grid.Col>}
        </Grid>
        <Grid columns={24} gutter={36}>
          {!isMobile && <Grid.Col span={24}>
            {isActive && <CodeEditor user={props.user}/>}
          </Grid.Col>}
          {!isMobile && <Grid.Col span={16}>
            <div className="user-orders-wrapper">
              <UserOrdersComponent
                symbol={symbol.code}
                owner={userid}
                closedOrders={closedOrders}
                setClosedOrders={setClosedOrders}
                openAskOrders={openAskOrders}
                setOpenAskOrders={setOpenAskOrders}
                openBidOrders={openBidOrders}
                setOpenBidOrders={setOpenBidOrders}
              />
            </div>
          </Grid.Col>}
          {!isMobile && <Grid.Col span={8}>
            <div className="order-book-wrapper">
              <OrderBook symbol={symbol.code} />
            </div>
          </Grid.Col>}
          {isMobile && <Grid.Col span={24}>
            <div className="user-orders-wrapper">
              <UserOrdersComponent
                symbol={symbol.code}
                owner={userid}
                closedOrders={closedOrders}
                setClosedOrders={setClosedOrders}
                openAskOrders={openAskOrders}
                setOpenAskOrders={setOpenAskOrders}
                openBidOrders={openBidOrders}
                setOpenBidOrders={setOpenBidOrders}
              />
            </div>
          </Grid.Col>}
          {isMobile && <Grid.Col span={24}>
            <div className="order-book-wrapper">
              <OrderBook symbol={symbol.code} />
            </div>
          </Grid.Col>}
        </Grid>
        
        {/* Holdings (Long Positions) Section */}
        {userid && (
          <Grid columns={24}>
            <Grid.Col span={24}>
              <LongPositionsComponent
                walletid={parseInt(userid)}
                symbol={symbol.code}
                onSellPosition={handleSellPosition}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Short Positions Section */}
        {userid && (
          <Grid columns={24}>
            <Grid.Col span={24}>
              <ShortPositionsComponent
                ownerId={parseInt(userid)}
                symbol={symbol.code}
                onCoverPosition={handleCoverPosition}
              />
            </Grid.Col>
          </Grid>
        )}
      </Stack>
    </div>
  );
}
